import { createSlice, Slice } from "@reduxjs/toolkit";
import { channelReducer } from "./reducers/channel.reducer.ts";
import { GeneralStatus } from "../../../enums/status.ts";
import {
  errorSubmitIbanReducer,
  submitIbanReducer,
} from "./reducers/iban.reducer.ts";
import { privacyReducer } from "./reducers/privacy.reducer.ts";
import { dealReducer } from "./reducers/deal.reducer.ts";
import { workflowReducer } from "./reducers/workflow.reducer.ts";
import { createDeal } from "../../actions/create-deal.action.ts";
import { createDealReducer } from "./reducers/create-deal.reducer.ts";
import { submitPrivacyConsents } from "../../actions/submit-privacy-consents.action.ts";
import { submitCouponCode } from "../../actions/submit-coupon-code.action.ts";
import { submitContacts } from "../../actions/submit-contacts.action.ts";
import { submitContactsReducer } from "./reducers/submit-contacts.reducer.ts";
import { submitCouponCodeReducer } from "./reducers/submit-coupon-code.reducer.ts";
import initStore from "../../actions/init-store.action.ts";
import initStoreReducer from "./reducers/init-store.reducer.ts";
import { getOffers } from "../../actions/get-offers.action.ts";
import { getOffersReducer } from "./reducers/get-offers.reducer.ts";
import { getConfig } from "../../actions/get-config.action.ts";
import { getConfigReducer } from "./reducers/get-config.reducer.ts";
import {
  ListDocumentResponse,
  LoanIntent,
  OfferResponse,
} from "../../../orchestrator-api-client";
import {
  DEFAULT_OFFER_CONFIG,
  OfferConfig,
} from "../../../features/offer/types/offer-config.ts";
import { selectOffer } from "../../actions/select-offer.action.ts";
import { selectOfferReducer } from "./reducers/select-offer.reducer.ts";
import { submitIban } from "../../actions/submit-iban.action.ts";
import { editOffer } from "../../actions/edit-offer.action.ts";
import {
  editOfferReducer,
  isLastEditOfferReducer,
} from "./reducers/edit-offer.reducer.ts";
import { offerReducer, offersReducer } from "./reducers/offer.reducer.ts";
import { getDocumentsList } from "../../actions/get-documents-list.action.ts";
import { getDocumentsListReducer } from "./reducers/get-documents-list.reducer.ts";
import { confirmOffer } from "../../actions/confirm-offer.action.ts";
import { confirmOfferReducer } from "./reducers/confirm-offer.reducer.ts";

export type Privacy = {
  // secciAcknowledgementDate?: Date | string;
  // contractualDocsAcknowledgementDate?: Date | string;
  // consensoComunicazioniCommerciali?: boolean | "";
  // consensoNewsletter?: boolean | "";
  // consensoSurvey?: boolean | "";
  // consensoCessioneDati?: boolean | "";
  // consensoDatiAis?: boolean | "";
  // consensoOpenBankingPsd2?: boolean | "";
  // consensoProfilazione?: boolean | "";
  // consensoOpenBanking?: boolean | "";
  // consensoKyc?: boolean | "";
  // consensoPrivacy?: boolean | "";
  // consensoTerms?: boolean | "";
  // privacyVideoSelfie?: Date | string;
  // facialRecognition?: Date | string;
};

export interface Offer {
  id: string;
  groupId: string;
  requestedAmount: number;
  installmentsCount: number;
  status: GeneralStatus;
  installmentsAmount?: number;
  tan?: number;
  taeg?: number;
  cpi?: string; //"METLIFE"
  discount?: string; // "UNIREC24"
}

export interface Iban {
  ibanCode: string;
  ibanAttempts: number;
}

export interface LoanState {
  channel: string;
  dealId: string;
  contactId: string;
  positionId: string;
  loanIntent?: LoanIntent;
  workflowId: string;
  privacy: Privacy;
  coupon: string;
  offersAttempts?: number;
  offer?: OfferResponse;
  offers?: Array<OfferResponse>;
  simulationOfferFailed?: boolean;
  iban: Iban;
  payrolls?: ListDocumentResponse;
  accountStatement?: ListDocumentResponse;
  confirmedOffer?: OfferResponse;
  childWorkflowId?: string;
  config?: OfferConfig;
}

const initialState: LoanState = {
  channel: "",
  dealId: "",
  positionId: "",
  contactId: "",
  privacy: {
    secciAcknowledgementDate: new Date().toISOString(),
    contractualDocsAcknowledgementDate: new Date().toISOString(),
    consensoComunicazioniCommerciali: "",
    consensoNewsletter: "",
    consensoSurvey: "",
    consensoCessioneDati: "",
    consensoDatiAis: "",
    consensoOpenBankingPsd2: "",
    consensoProfilazione: "",
    consensoOpenBanking: "",
    consensoKyc: "",
    consensoPrivacy: "",
    consensoTerms: "",
    privacyVideoSelfie: new Date().toISOString(),
    facialRecognition: new Date().toISOString(),
  },
  coupon: "",
  workflowId: "",
  iban: {
    ibanCode: "",
    ibanAttempts: 0,
  },
  config: DEFAULT_OFFER_CONFIG,
};

const loanSlice: Slice<LoanState> = createSlice({
  name: "loan",
  initialState,
  reducers: {
    setChannel: channelReducer,
    setDealId: dealReducer,
    setWorkflowId: workflowReducer,
    setPrivacy: privacyReducer,
    setOffer: offerReducer,
    setOffers: offersReducer,
    setIban: submitIbanReducer,
    setIsLastEditOfferValid: isLastEditOfferReducer,
  },
  extraReducers: (builder) => {
    builder.addCase(initStore.fulfilled, initStoreReducer);
    builder.addCase(createDeal.fulfilled, createDealReducer);
    builder.addCase(submitPrivacyConsents.fulfilled, privacyReducer);
    builder.addCase(submitContacts.fulfilled, submitContactsReducer);
    builder.addCase(submitCouponCode.fulfilled, submitCouponCodeReducer);
    builder.addCase(submitIban.fulfilled, submitIbanReducer);
    builder.addCase(submitIban.rejected, errorSubmitIbanReducer);

    //offers
    builder.addCase(getOffers.fulfilled, getOffersReducer);
    builder.addCase(getConfig.fulfilled, getConfigReducer);
    builder.addCase(selectOffer.fulfilled, selectOfferReducer);
    builder.addCase(confirmOffer.fulfilled, confirmOfferReducer);
    builder.addCase(editOffer.fulfilled, editOfferReducer);

    builder.addCase(getDocumentsList.fulfilled, getDocumentsListReducer);
  },
});

export const { setDealId, setIban, setIsLastEditOfferValid, setOffer } =
  loanSlice.actions;

const loanReducer = loanSlice.reducer;
export default loanReducer;
